import React from 'react';
import { Sparkles, Shirt, User, Settings, LogOut, Plus, BookOpen, Layers } from 'lucide-react';
import { User as UserType } from '../types';

interface NavbarProps {
  user: UserType | null;
  activeTab: 'closet' | 'studio' | 'lookbook';
  setActiveTab: (tab: 'closet' | 'studio' | 'lookbook') => void;
  onOpenAddItem: () => void;
  onOpenBodyProfiles: () => void;
  onOpenAccount: () => void;
  onOpenSettings: () => void;
  onLogout?: () => void;
  hasGeminiKey: boolean;
  clothesCount: number;
}

export const Navbar: React.FC<NavbarProps> = ({
  user,
  activeTab,
  setActiveTab,
  onOpenAddItem,
  onOpenBodyProfiles,
  onOpenAccount,
  onOpenSettings,
  hasGeminiKey,
  clothesCount,
}) => {
  return (
    <header className="sticky top-0 z-40 bg-slate-900/80 backdrop-blur-md border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Brand & Logo */}
        <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setActiveTab('closet')}>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-brand-600 to-indigo-500 flex items-center justify-center shadow-lg shadow-brand-500/20 ring-1 ring-white/20">
            <Shirt className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center space-x-1.5">
              <span className="font-extrabold text-lg tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-200 to-slate-400">
                FitCheck
              </span>
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-brand-500/20 text-brand-300 font-semibold border border-brand-500/30 flex items-center gap-0.5">
                <Sparkles className="w-2.5 h-2.5" /> AI
              </span>
            </div>
            <p className="text-[11px] text-slate-400 font-medium hidden sm:block">
              Virtual Wardrobe • closet.tiger-home.com
            </p>
          </div>
        </div>

        {/* Desktop Navigation Tabs */}
        <nav className="hidden md:flex items-center bg-slate-950/60 p-1 rounded-xl border border-slate-800/80">
          <button
            onClick={() => setActiveTab('closet')}
            className={`flex items-center space-x-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'closet'
                ? 'bg-slate-800 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Shirt className="w-4 h-4" />
            <span>Closet ({clothesCount})</span>
          </button>

          <button
            onClick={() => setActiveTab('studio')}
            className={`flex items-center space-x-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'studio'
                ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white shadow-sm shadow-brand-500/20'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Sparkles className="w-4 h-4 text-brand-300" />
            <span>AI Try-On Studio</span>
          </button>

          <button
            onClick={() => setActiveTab('lookbook')}
            className={`flex items-center space-x-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'lookbook'
                ? 'bg-slate-800 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            <span>Lookbook</span>
          </button>
        </nav>

        {/* Right actions */}
        <div className="flex items-center space-x-2 sm:space-x-3">
          {/* Quick Add Garment */}
          <button
            onClick={onOpenAddItem}
            className="flex items-center space-x-1.5 bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white px-3.5 py-1.5 rounded-xl text-sm font-semibold shadow-md shadow-brand-600/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Clothes</span>
          </button>

          {/* User Body Profile Trigger */}
          <button
            onClick={onOpenBodyProfiles}
            title="My Body Photo Profile"
            className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700 text-slate-300 hover:text-white transition-colors"
          >
            <Layers className="w-4 h-4" />
          </button>

          {/* Settings Trigger */}
          <button
            onClick={onOpenSettings}
            title="Settings & Google AI"
            className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700 text-slate-300 hover:text-white transition-colors relative"
          >
            <Settings className="w-4 h-4" />
            {!hasGeminiKey && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full animate-ping" />
            )}
            {!hasGeminiKey && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full" />
            )}
          </button>

          {/* User Account Trigger */}
          {user && (
            <button
              onClick={onOpenAccount}
              title="Account & Profiles"
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700 text-slate-200 hover:text-white transition-colors"
            >
              <User className="w-4 h-4 text-brand-400" />
              <span className="text-xs font-bold hidden sm:inline">@{user.username}</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
